/**
 * Fensterbau Franken - Door Upsells Script
 * 
 * Custom theme-level script for store: qe9nvq-7c.myshopify.com
 * 
 * Adds upsell/surcharge products to the cart alongside the main door product
 * based on selected configurator options (dropdowns, checkboxes, swatches).
 * 
 * In default mode the Shopify ATC form submits normally (sendConfig is NOT used).
 * This script intercepts the form submission and injects hidden <input> elements
 * for upsell products — the same mechanism the configurator uses for text upsells.
 * It also hooks into updateConfigurator() to display surcharge prices in real-time.
 * 
 * SETUP:
 * 1. Create upsell products in Shopify Admin (one per surcharge option value)
 * 2. Replace all "VARIANT_ID_..." placeholders below with real Shopify variant IDs
 * 3. Upload this file to the theme's assets/ folder
 * 4. Include in theme.liquid or via Custom Liquid block:
 *    <script src="{{ 'fensterbau-franken-upsells.js' | asset_url }}" defer></script>
 */
(function () {
  'use strict';

  const STORE_DOMAIN = 'qe9nvq-7c.myshopify.com';

  // ============================================================
  // UPSELL CONFIGURATION
  // 
  // Maps option names + selected values to Shopify product variant IDs.
  // Only entries with a surcharge (non-free options) are listed.
  // 
  // IMPORTANT: Replace all "VARIANT_ID_..." values with the actual
  // numeric Shopify variant IDs after creating the products.
  // ============================================================

  const UPSELL_CONFIG = {

    // --- Dropdown / Radio options ---
    // Structure: optionName -> { selectedValue -> { variantId, price } }
    // The selectedValue is matched against the option's data-option-value / data-option-readable
    dropdown: {
      "Farbe innen": {
        // "Farbig (Farbe wie außen)" is included in the standard price -> no entry needed
        "Innen: Weiß RAL 9016 Verkehrsweiß glänzend (29/10950)": {
          variantId: "56766232985973",
          price: 50
        }
      },
      "Verriegelung": {
        // "BT 5 5-FACH VERRIEGELUNG MIT RUNDBOLZEN UND HAKEN – EDELSTAHL" is standard/free -> no entry
        "BTS 5 6-FACH VERRIEGELUNG MIT RUNDBOLZEN UND HAKEN MIT TÜRSPALTSICHERUNG – EDELSTAHL": {
          variantId: "56766256349557",
          price: 171
        },
        "ATM 5 5-PUNKT AUTOMATIK-MEHRFACHVERRIEGELUNG mit Rundbolzen und Haken": {
          variantId: "56766264934773",
          price: 125
        },
        "BT 1022 5-FACH BOLZEN-SCHWENKRIEGEL MIT ELEKTROMECHANISCHEM ANTRIEB - EDELSTAHL / KABELÜBERGANG / TRAFO / COMFORT B": {
          variantId: "56766285447541",
          price: 1184
        },
        "BT 670 INOSMART SYSTEM - FINGERSCAN, Bluetooth, CODETASTER / 5-FACH VERRIEGELUNG MIT ELEKTR. ANTRIEB / KABELÜBERGANG / TRAFO": {
          variantId: "56766295703925",
          price: 1362
        }
      }
    },

    // --- Checkbox options ---
    // Structure: optionName -> { variantId, price }
    // The upsell product is added when the checkbox is checked
    checkbox: {
      "E-Öffner": {
        variantId: "56766064263541",
        price: 106
      },
      "Wetterschenkel": {
        variantId: "56766066000245",
        price: 114
      },
      "Premium-Profilzylinder: Sicherheits-Profilzylinder K 100": {
        variantId: "56766069932405",
        price: 96
      }
    },

    // --- Swatch / Button / Image Swatch options ---
    // Structure: optionName -> { selectedValue -> { variantId, price } }
    // Works for variantswatch, imageswatch, and colorswatch option types
    swatch: {
      "Griff innen": {
        // "Griff: GA 190 Standard Drücker Edelstahl" is included in the base price -> no entry needed
        "Griff: ZIE 11": { variantId: "56767466733941", price: 89 },
        "Griff: ZIE 12": { variantId: "56767493505397", price: 89 },
        "Griff: ZIE 13": { variantId: "56767510086005", price: 89 },
        "Griff: ZIE 14": { variantId: "56767526044021", price: 89 }
      }
    }
  };


  // ============================================================
  // INTERNAL HELPERS
  // ============================================================

  /**
   * Finds a matching upsell entry for a given option name, type, and selected value.
   * Tries exact match first, then case-insensitive, then partial/contains match.
   * 
   * @param {string} optionName - The data-option-name attribute value
   * @param {string} optionType - The data-option-type attribute value  
   * @param {string} selectedValue - The selected value (data-option-value or data-option-readable)
   * @returns {{ variantId: string, price: number } | null}
   */
  function findUpsellMatch(optionName, optionType, selectedValue) {
    if (!optionName || !selectedValue) return null;

    let configSection = null;

    // Determine which config section to use based on option type
    if (optionType === 'dropdown' || optionType === 'radio') {
      configSection = UPSELL_CONFIG.dropdown;
    } else if (optionType === 'checkbox') {
      // Checkbox uses a simpler structure (no value matching needed)
      const entry = UPSELL_CONFIG.checkbox[optionName];
      if (entry && selectedValue === 'yes') {
        return entry;
      }
      // Also try partial name match for checkboxes
      for (const [key, val] of Object.entries(UPSELL_CONFIG.checkbox)) {
        if (namesMatch(key, optionName) && selectedValue === 'yes') {
          return val;
        }
      }
      return null;
    } else if (optionType === 'variantswatch' || optionType === 'imageswatch' || optionType === 'colorswatch') {
      configSection = UPSELL_CONFIG.swatch;
    } else {
      return null;
    }

    if (!configSection) return null;

    // Find the option name in the config (with flexible matching)
    let valueMap = null;
    if (configSection[optionName]) {
      valueMap = configSection[optionName];
    } else {
      for (const key of Object.keys(configSection)) {
        if (namesMatch(key, optionName)) {
          valueMap = configSection[key];
          break;
        }
      }
    }

    if (!valueMap) return null;

    // Find the selected value in the value map (with flexible matching)
    return findInValueMap(valueMap, selectedValue);
  }

  /**
   * Checks if two option names match (case-insensitive, trimmed).
   */
  function namesMatch(a, b) {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
  }

  /**
   * Searches a value map for a matching entry using progressively
   * looser matching strategies.
   * 
   * @param {Object} valueMap - { valueName: { variantId, price } }
   * @param {string} selectedValue - The value to find
   * @returns {{ variantId: string, price: number } | null}
   */
  function findInValueMap(valueMap, selectedValue) {
    if (!valueMap || !selectedValue) return null;

    const trimmed = selectedValue.trim();

    // 1. Exact match
    if (valueMap[trimmed]) return valueMap[trimmed];

    // 2. Case-insensitive match
    for (const [key, entry] of Object.entries(valueMap)) {
      if (key.trim().toLowerCase() === trimmed.toLowerCase()) {
        return entry;
      }
    }

    // 3. Contains match (key is contained in value or vice versa)
    for (const [key, entry] of Object.entries(valueMap)) {
      const keyTrimmed = key.trim().toLowerCase();
      const valueTrimmed = trimmed.toLowerCase();
      if (valueTrimmed.includes(keyTrimmed) || keyTrimmed.includes(valueTrimmed)) {
        return entry;
      }
    }

    return null;
  }


  // ============================================================
  // UPSELL COLLECTION
  // ============================================================

  /**
   * Reads all .config_option elements from the DOM and determines
   * which upsell products should be added to the cart.
   * 
   * @returns {Array<{ variantId: string, price: number, optionName: string }>}
   */
  function collectUpsells() {
    const upsells = [];
    const configOptions = document.querySelectorAll('.config_option');

    configOptions.forEach(function (configOption) {
      // Skip hidden options (e.g., hidden by rules)
      if (configOption.style.display === 'none') return;

      const optionName = configOption.getAttribute('data-option-name');
      const optionType = configOption.getAttribute('data-option-type');
      const optionValue = configOption.getAttribute('data-option-value');
      const optionReadable = configOption.getAttribute('data-option-readable');

      // Skip empty values
      if (!optionValue && !optionReadable) return;

      // Try matching with value first, then readable
      var match = findUpsellMatch(optionName, optionType, optionValue);
      if (!match && optionReadable && optionReadable !== optionValue) {
        match = findUpsellMatch(optionName, optionType, optionReadable);
      }

      if (match && match.variantId && !match.variantId.startsWith('VARIANT_ID_')) {
        upsells.push({
          variantId: match.variantId,
          price: match.price || 0,
          optionName: optionName
        });
      }
    });

    return upsells;
  }


  // ============================================================
  // PRICE PREVIEW
  // ============================================================

  /**
   * Calculates the total surcharge from all currently selected options.
   * Uses the UPSELL_CONFIG prices (not the DOM surcharge attributes).
   * 
   * @returns {number} Total surcharge in EUR
   */
  function calculateTotalSurcharge() {
    var total = 0;
    var configOptions = document.querySelectorAll('.config_option');

    configOptions.forEach(function (configOption) {
      if (configOption.style.display === 'none') return;

      var optionName = configOption.getAttribute('data-option-name');
      var optionType = configOption.getAttribute('data-option-type');
      var optionValue = configOption.getAttribute('data-option-value');
      var optionReadable = configOption.getAttribute('data-option-readable');

      if (!optionValue && !optionReadable) return;

      var match = findUpsellMatch(optionName, optionType, optionValue);
      if (!match && optionReadable && optionReadable !== optionValue) {
        match = findUpsellMatch(optionName, optionType, optionReadable);
      }

      if (match && match.price) {
        total += match.price;
      }
    });

    return total;
  }

  /**
   * Creates or updates the surcharge price summary element below
   * the configurator options. Shows base price + individual surcharges + total.
   */
  function updateUpsellPricePreview() {
    // Only run in default mode for this store
    if (typeof productConfiguration === 'undefined') return;
    if (productConfiguration.shop !== STORE_DOMAIN) return;
    if (productConfiguration.priceMode !== 'default') return;

    var totalSurcharge = calculateTotalSurcharge();

    // Get or create the surcharge summary container
    var summaryEl = document.getElementById('ff-upsell-price-summary');
    if (!summaryEl) {
      summaryEl = document.createElement('div');
      summaryEl.id = 'ff-upsell-price-summary';
      summaryEl.style.cssText = 'margin-top: 16px; padding: 12px 16px; border: 1px solid #e0e0e0; border-radius: 8px; background: #fafafa; font-size: 14px;';

      // Insert after the options container
      var optionsContainer = document.getElementById('vz_options_container');
      if (optionsContainer && optionsContainer.parentElement) {
        optionsContainer.parentElement.insertBefore(summaryEl, optionsContainer.nextSibling);
      }
    }

    // Get base price from Shopify
    var basePrice = 0;
    try {
      basePrice = window.ShopifyAnalytics.meta.product.variants[0].price / 100;
    } catch (e) {
      // Fallback: try reading price from the selected variant
      try {
        var priceEl = document.querySelector('.price__regular .price-item--regular') ||
          document.querySelector('.product__price .price-item') ||
          document.querySelector('[data-product-price]');
        if (priceEl) {
          basePrice = parseFloat(priceEl.textContent.replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
        }
      } catch (e2) { /* ignore */ }
    }

    var totalPrice = basePrice + totalSurcharge;

    // Build the summary HTML
    var html = '';

    if (totalSurcharge > 0) {
      // Collect individual surcharges for display
      var surchargeLines = [];
      var configOptions = document.querySelectorAll('.config_option');

      configOptions.forEach(function (configOption) {
        if (configOption.style.display === 'none') return;

        var optionName = configOption.getAttribute('data-option-name');
        var optionType = configOption.getAttribute('data-option-type');
        var optionValue = configOption.getAttribute('data-option-value');
        var optionReadable = configOption.getAttribute('data-option-readable');

        if (!optionValue && !optionReadable) return;

        var match = findUpsellMatch(optionName, optionType, optionValue);
        if (!match && optionReadable && optionReadable !== optionValue) {
          match = findUpsellMatch(optionName, optionType, optionReadable);
        }

        if (match && match.price > 0) {
          surchargeLines.push({
            name: optionName,
            value: optionReadable || optionValue,
            price: match.price
          });
        }
      });

      html += '<div style="margin-bottom: 8px; color: #666;">';
      html += '<div style="display: flex; justify-content: space-between; margin-bottom: 4px;">';
      html += '<span>Grundpreis:</span>';
      html += '<span>' + formatPrice(basePrice) + '</span>';
      html += '</div>';

      surchargeLines.forEach(function (line) {
        html += '<div style="display: flex; justify-content: space-between; margin-bottom: 2px; font-size: 13px;">';
        html += '<span style="color: #888;">+ ' + escapeHtml(line.name) + ':</span>';
        html += '<span style="color: #888;">+ ' + formatPrice(line.price) + '</span>';
        html += '</div>';
      });

      html += '</div>';
      html += '<div style="display: flex; justify-content: space-between; font-weight: bold; border-top: 1px solid #ddd; padding-top: 8px; font-size: 16px;">';
      html += '<span>Gesamtpreis:</span>';
      html += '<span>' + formatPrice(totalPrice) + '</span>';
      html += '</div>';
    }

    summaryEl.innerHTML = html;
    summaryEl.style.display = totalSurcharge > 0 ? 'block' : 'none';

    // Also update individual surcharge labels on each option
    updateIndividualSurchargeLabels();
  }

  /**
   * Updates the .vz_surcharge spans on each config option to show
   * the surcharge price for the currently selected value.
   */
  function updateIndividualSurchargeLabels() {
    var configOptions = document.querySelectorAll('.config_option');

    configOptions.forEach(function (configOption) {
      var surchargeSpan = configOption.querySelector('.vz_surcharge');
      if (!surchargeSpan) return;

      var optionName = configOption.getAttribute('data-option-name');
      var optionType = configOption.getAttribute('data-option-type');
      var optionValue = configOption.getAttribute('data-option-value');
      var optionReadable = configOption.getAttribute('data-option-readable');

      var match = findUpsellMatch(optionName, optionType, optionValue);
      if (!match && optionReadable && optionReadable !== optionValue) {
        match = findUpsellMatch(optionName, optionType, optionReadable);
      }

      if (match && match.price > 0) {
        surchargeSpan.textContent = '(+ ' + formatPrice(match.price) + ')';
        surchargeSpan.style.color = '#888';
        surchargeSpan.style.marginLeft = '6px';
        surchargeSpan.style.fontWeight = 'normal';
        surchargeSpan.style.fontSize = '13px';
      } else {
        surchargeSpan.textContent = '';
      }
    });
  }

  /**
   * Formats a price in EUR.
   * @param {number} price 
   * @returns {string}
   */
  function formatPrice(price) {
    return price.toFixed(2).replace('.', ',') + ' \u20AC';
  }

  /**
   * Escapes HTML special characters.
   * @param {string} str 
   * @returns {string}
   */
  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }


  // ============================================================
  // FORM SUBMISSION INTERCEPTION
  // ============================================================

  /** @type {HTMLFormElement|null} */
  var shopifyForm = null;

  /**
   * Finds the Shopify ATC form (kept intact in default mode) and
   * adds a submit listener that syncs upsell hidden inputs right
   * before the form is actually submitted.
   */
  function interceptFormSubmission() {
    // The configurator marks the form it keeps with this data attribute
    shopifyForm = document.querySelector('form[data-vz-default-form-processing="true"]');
    if (!shopifyForm) {
      // Fallback: find any /cart/add form
      shopifyForm = document.querySelector('form[action*="/cart/add"]');
    }

    if (!shopifyForm) {
      console.warn('[Fensterbau Upsells] Could not find Shopify ATC form — upsells will NOT be added to cart.');
      return;
    }

    console.log('[Fensterbau Upsells] Found Shopify ATC form, attaching submit listener:', shopifyForm);

    shopifyForm.addEventListener('submit', function () {
      // Sync upsell hidden inputs right before the form is posted
      console.log('[Fensterbau Upsells] Form submit — syncing upsell inputs');
      syncUpsellFormInputs();
    });

    // Also do an initial sync
    syncUpsellFormInputs();
  }

  /**
   * Removes any previously injected upsell <input>s from the form,
   * then adds new ones for every currently active upsell.
   *
   * Uses the same pattern the configurator already uses for text-option
   * upsells: hidden inputs with name="items[N][id]" / items[N][quantity].
   * We start at index 10 to avoid conflicts with text-upsell inputs at index 1.
   */
  function syncUpsellFormInputs() {
    if (!shopifyForm) return;

    // 1. Remove our previously injected inputs
    shopifyForm.querySelectorAll('.ff-upsell-input').forEach(function (el) { el.remove(); });
    // Also remove any that live outside the form (linked by form attribute)
    document.querySelectorAll('.ff-upsell-input').forEach(function (el) { el.remove(); });

    // 2. Collect active upsells
    var upsells = collectUpsells();
    console.log('[Fensterbau Upsells] syncUpsellFormInputs — active upsells:', upsells.length, upsells);

    if (upsells.length === 0) return;

    // 3. Read current quantity from the form
    var quantityInput = shopifyForm.querySelector('input[name="quantity"]');
    var quantity = quantityInput ? quantityInput.value : '1';

    // 4. Inject hidden inputs for each upsell
    upsells.forEach(function (upsell, index) {
      // Start indices at 10 to stay clear of text-upsell inputs (items[1])
      var itemIndex = index + 10;

      var idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = 'items[' + itemIndex + '][id]';
      idInput.value = upsell.variantId;
      idInput.className = 'ff-upsell-input';

      var qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = 'items[' + itemIndex + '][quantity]';
      qtyInput.value = quantity;
      qtyInput.className = 'ff-upsell-input';

      shopifyForm.appendChild(idInput);
      shopifyForm.appendChild(qtyInput);

      console.log('[Fensterbau Upsells] Injected hidden input items[' + itemIndex + ']: variant=' + upsell.variantId + ' (' + upsell.optionName + ')');
    });
  }


  // ============================================================
  // UPDATECONFIGURATOR HOOK
  // ============================================================

  /**
   * Wraps the original updateConfigurator function to update
   * the upsell price preview and sync form inputs after each
   * configurator update.
   */
  function hookUpdateConfigurator() {
    var originalUpdateConfigurator = window.updateConfigurator;

    window.updateConfigurator = function () {
      // Call the original first
      originalUpdateConfigurator();

      // Then update our surcharge display and form inputs
      if (typeof productConfiguration !== 'undefined' &&
        productConfiguration.shop === STORE_DOMAIN &&
        productConfiguration.priceMode === 'default') {
        updateUpsellPricePreview();
        syncUpsellFormInputs();
      }
    };
  }


  // ============================================================
  // INITIALIZATION
  // ============================================================

  /**
   * Polls until the configurator's global functions are available,
   * then hooks into them.
   */
  function init() {
    // Check if the required globals exist AND are populated
    if (typeof updateConfigurator === 'undefined' ||
      typeof productConfiguration === 'undefined' ||
      typeof selectedOptions === 'undefined' ||
      !productConfiguration.shop) {
      // Not ready yet, retry (productConfiguration.shop is set after initializeProductConfigurator runs)
      setTimeout(init, 200);
      return;
    }

    // Guard: only activate for this store
    if (productConfiguration.shop !== STORE_DOMAIN) {
      console.log('[Fensterbau Upsells] Wrong store (' + productConfiguration.shop + '), skipping');
      return;
    }

    // Guard: only needed for default mode
    if (productConfiguration.priceMode !== 'default') {
      console.log('[Fensterbau Upsells] Not in default mode (' + productConfiguration.priceMode + '), skipping');
      return;
    }

    console.log('[Fensterbau Upsells] Initializing upsell script for', STORE_DOMAIN);

    // Intercept the Shopify ATC form to inject upsell hidden inputs
    interceptFormSubmission();

    // Hook into updateConfigurator to update price preview + sync inputs
    hookUpdateConfigurator();

    // Run an initial price preview update
    updateUpsellPricePreview();

    console.log('[Fensterbau Upsells] Initialization complete');
  }

  // Start initialization when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      // Small delay to ensure product-configurator.js has run
      setTimeout(init, 500);
    });
  } else {
    // DOM already loaded, start with a delay
    setTimeout(init, 500);
  }

})();

